import React, { useContext, useMemo, ComponentType } from 'react';
import { AppContext } from './AppContext';
import { RootState, AppDispatch, AppAction } from '../types';

type ActionCreator = (...args: unknown[]) => AppAction | ((dispatch: AppDispatch) => void | Promise<void>);

interface ConnectOptions<TStateProps, TDispatchProps extends Record<string, ActionCreator>, TOwnProps> {
  mapStateToProps?: (state: RootState, ownProps: TOwnProps) => TStateProps;
  mapDispatchToProps?: TDispatchProps;
  component: ComponentType<TStateProps & { [K in keyof TDispatchProps]: (...args: Parameters<TDispatchProps[K]>) => void } & TOwnProps>;
}

export function connect<
  TStateProps = Record<string, never>,
  TDispatchProps extends Record<string, ActionCreator> = Record<string, never>,
  TOwnProps = Record<string, never>
>({
  mapStateToProps = () => ({} as TStateProps),
  mapDispatchToProps = {} as TDispatchProps,
  component,
}: ConnectOptions<TStateProps, TDispatchProps, TOwnProps>): React.FC<TOwnProps> {
  const Connect: React.FC<TOwnProps> = (ownProps) => {
    const context = useContext(AppContext);

    const dispatchFuncs = useMemo(() => {
      const funcs: Record<string, (...args: unknown[]) => void> = {};

      Object.keys(mapDispatchToProps).forEach((key) => {
        const oldFunc = mapDispatchToProps[key];
        const newFunc = (...args: unknown[]): void => {
          const dispatchFunc = oldFunc(...args);

          if (typeof dispatchFunc === 'object' && 'type' in dispatchFunc) {
            context.dispatch(dispatchFunc as AppAction);
          } else if (typeof dispatchFunc === 'function') {
            const result = dispatchFunc(context.dispatch);

            if (result && typeof result === 'object' && 'then' in result) {
              (result as Promise<AppAction | void>).then((dispatchObject) => {
                if (dispatchObject && typeof dispatchObject === 'object' && 'type' in dispatchObject) {
                  context.dispatch(dispatchObject as AppAction);
                }
              });
            }
          }
        };
        funcs[key] = newFunc;
      });

      return funcs;
    }, []);

    const props = useMemo(() => {
      return {
        ...ownProps,
        ...mapStateToProps(context.state, ownProps),
        ...dispatchFuncs,
      };
    }, [ownProps, context.state, dispatchFuncs]);

    return React.createElement(component, props as TStateProps & { [K in keyof TDispatchProps]: (...args: Parameters<TDispatchProps[K]>) => void } & TOwnProps);
  };

  return React.memo(Connect) as React.FC<TOwnProps>;
}
